<template>
  <div
    id="analytics_dashboard_card"
    :class="isKpiChart ? 'self-center' : 'self-start'"
  >
    <div v-if="isConfigured" id="chart_rendering_section" class="w-full">
      <SkeletonLoader v-if="isLoading" :page="loaderPages.CHART" />

      <ChartSection
        v-else-if="hasChartData || htmlContent"
        :content="htmlContent"
        :chart-type="chartType"
        :chart-data="chartData"
        :chart-config="chartConfig"
        :dashboard-type="effectiveDashboardType"
        :source="`analytics_dashboard_card_${section.config?.dashboard_id}`"
        :height="chartHeight"
        class="w-full"
        @page-change="handlePageChange"
      />

      <EmptyScreen
        v-else
        source="analytics_dashboard_card"
        text="No data found"
      />
    </div>

    <div
      v-else
      id="dashboard_selection_dashboard"
      class="flex flex-col justify-between h-full gap-6"
    >
      <div class="flex flex-col gap-6">
        <div id="card_name" class="flex flex-col gap-[6px]">
          <span class="text-[13px] secondary_color"> Card Name </span>
          <a-input
            v-model:value="cardName"
            class="input_medium"
            placeholder="Enter a card name"
          />
        </div>

        <div id="select_dashboard" class="flex flex-col gap-[6px]">
          <span class="text-[13px] secondary_color">
            Choose a dashboard to display
          </span>

          <a-select
            v-model:value="selectedDashboardID"
            class="w-full"
            placeholder="Select a dashboard"
            show-search
            :filter-option="filterDashboardOption"
          >
            <template #suffixIcon>
              <ThemeImage
                :icon="imageNames.BOTTOM_ARROW"
                alt="bottom_white_arrow"
                class="size-4"
              />
            </template>

            <a-select-option
              v-for="(dashboard, index) in dashboardsList"
              :key="`${dashboard.id}_number_${index}`"
              :value="dashboard.id"
              :label="dashboard.name"
            >
              {{ dashboard.name }}
            </a-select-option>
          </a-select>
        </div>
      </div>

      <a-button
        class="primary_medium w-fit self-end"
        :loading="saving"
        :disabled="!selectedDashboardID || !cardName"
        @click="handleSaveChanges"
      >
        {{ saving ? "Saving.." : "Save changes" }}
      </a-button>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { analyticsEditorTypes, paginationDefaults } from "@/src/constants/analyticConstants.js";
import store from "@/src/store/index.js";
import eventBus from "@/src/utils/eventBus.js";
import AnalyticService from "@/src/api/AnalyticService.js";
import DashboardService from "@/src/api/DashboardService.js";
import ThemeImage from "@/src/components/ThemeImage.vue";
import ChartSection from "@/src/components/analytics_components/v3/chart/ChartSection.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import EmptyScreen from "@/src/components/EmptyScreen.vue";

const props = defineProps({
  source: { type: String, required: true },
  section: { type: Object, required: true },
  rowIndex: { type: Number, required: true },
  sectionIndex: { type: Number, required: true },
  globalUserId: { type: Number, default: null },
  globalUserIds: { type: Array, default: () => [] },
  globalUserOperator: { type: String, default: null },
  dashboardFilters: { type: Object, default: () => ({}) },
});

const saving = ref(false);
const isLoading = ref(false);
const cardName = ref(props.section.config?.card_name || "");
const htmlContent = ref(null);
const dashboardsList = ref([]);
const selectedDashboardID = ref(props.section.config?.dashboard_id || null);
const chartType = ref(null);
const chartData = ref({});
const chartConfig = ref(null);
const configLoaded = ref(false);

const dashboardDetails = computed(
  () => store.getters["dashboard/dashboardDetails"],
);

const isEditing = computed(() =>
  store.getters["dashboard/isSectionEditing"](props.rowIndex, props.sectionIndex),
);

const hasChartData = computed(() => {
  const data = chartData.value;
  if (!data) return false;
  return (
    (data.categories && data.categories.length > 0) ||
    (data.series && data.series.length > 0) ||
    (data.labels && data.labels.length > 0) ||
    (data.kpis && data.kpis.length > 0) ||
    (data.rows && data.rows.length > 0) ||
    (data.stages && data.stages.length > 0)
  );
});

const isKpiChart = computed(() => {
  return chartType.value?.toLowerCase()?.includes("kpi");
});

const isTableChart = computed(() => {
  return chartType.value?.toLowerCase()?.includes("table");
});

const chartHeight = computed(() => {
  if (isKpiChart.value || isTableChart.value) {
    return "auto";
  }
  if (htmlContent.value) {
    return "auto";
  }
  return 570;
});

const effectiveDashboardType = computed(() => {
  if (configLoaded.value && hasChartData.value && chartType.value) {
    return analyticsEditorTypes.MANUAL;
  }
  if (htmlContent.value) {
    return analyticsEditorTypes.AI;
  }
  return analyticsEditorTypes.MANUAL;
});

const computedUserId = computed(() => {
  return props.globalUserId;
});

const isConfigured = computed(() => {
  return (
    !!props.section.config?.dashboard_id && !isEditing.value
  );
});

const currentChartID = computed(() => {
  const dashboard = dashboardsList.value?.find(
    (dashboard) =>
      dashboard.id ===
      (props.section.config?.dashboard_id || selectedDashboardID.value),
  );
  return dashboard?.current_chart_id || null;
});

const currentConfigurationID = computed(() => {
  const dashboardId =
    props.section.config?.dashboard_id || selectedDashboardID.value;
  const dashboard = dashboardsList.value?.find(
    (dashboard) => dashboard.id === dashboardId,
  );
  return dashboard?.current_configuration_id || null;
});

const filterDashboardOption = (input, option) => {
  try {
    const label = option.label || "";
    return String(label).toLowerCase().includes(input.toLowerCase());
  } catch (error) {
    console.error(
      "An error occurred while filtering dashboard option: ",
      error,
    );
    return false;
  }
};

const handleSaveChanges = async () => {
  try {
    saving.value = true;

    if (props.rowIndex !== null && props.sectionIndex !== null) {
      await store.dispatch("dashboard/updateSectionConfig", {
        rowIndex: props.rowIndex,
        sectionIndex: props.sectionIndex,
        config: {
          card_name: cardName.value,
          dashboard_id: selectedDashboardID.value,
        },
      });

      store.dispatch("dashboard/clearSectionEditing", {
        rowIndex: props.rowIndex,
        sectionIndex: props.sectionIndex,
      });

      const updatedLayout = store.getters["dashboard/dashboardLayout"];

      const payload = {
        name: dashboardDetails.value?.name,
        layout: updatedLayout,
      };

      const response = await DashboardService.builder.updateDashboard(
        dashboardDetails.value?.id,
        payload,
      );

      if (response?.success) {
        await store.dispatch("dashboard/setDashboardDetails", response.data);
        await toFetchDashboards();

        if (currentChartID.value) {
          await toFetchChartByID();
        } else if (currentConfigurationID.value) {
          await toGetDashboardConfigurationByID();
        }
      }
    }
  } catch (error) {
    console.error("An error occurred while saving changes: ", error);
  } finally {
    saving.value = false;
  }
};

const toFetchDashboards = async () => {
  try {
    const response = await AnalyticService.V3.fetchDashboards();

    if (response?.success) {
      dashboardsList.value = response?.data;

      if (
        props.section.config?.dashboard_id &&
        dashboardsList.value?.length > 0
      ) {
        if (currentChartID.value) {
          await toFetchChartByID();
        } else if (currentConfigurationID.value) {
          await toGetDashboardConfigurationByID();
        }
      }
    }
  } catch (error) {
    console.error("An error occurred while fetching dashboards: ", error);
  }
};

const injectBodyStyles = (html) => {
  try {
    if (!html) return html;

    const styleTag =
      "<style>body { margin: 0 !important; padding: 0 !important; }</style>";

    if (/<head[^>]*>/i.test(html)) {
      return html.replace(/<head([^>]*)>/i, `<head$1>${styleTag}`);
    } else if (/<body[^>]*>/i.test(html)) {
      return html.replace(/<body([^>]*)>/i, `${styleTag}<body$1>`);
    } else {
      return styleTag + html;
    }
  } catch (error) {
    console.error("An error occurred while injecting body styles: ", error);
  }
};

const toFetchChartByID = async () => {
  if (!currentChartID.value) return;

  try {
    isLoading.value = true;
    const response = await AnalyticService.V3.fetchChartByID(
      currentChartID.value,
      {},
    );

    if (response?.success) {
      const data = response?.data;
      const rawHtml = data?.view_html || null;
      htmlContent.value = injectBodyStyles(rawHtml);
    }
  } catch (error) {
    console.error("An error occurred while fetching chart by ID: ", error);
  } finally {
    isLoading.value = false;
    eventBus.emit("chart-data-loaded", {
      key: `${props.rowIndex}_${props.sectionIndex}`,
      total: 0,
    });
  }
};

const toGetDashboardConfigurationByID = async (extraParams = {}, { silent = false } = {}) => {
  const dashboardId =
    props.section.config?.dashboard_id || selectedDashboardID.value;

  if (!dashboardId) return;

  let chartTotal = 0;
  try {
    if (!silent) isLoading.value = true;
    const params = { ...extraParams };
    const userOp = props.globalUserOperator || "is";
    if (userOp === "is_empty" || userOp === "is_not_empty") {
      params.user_id_operator = userOp;
    } else if (props.globalUserIds?.length > 0) {
      params.user_ids = props.globalUserIds.join(",");
      if (userOp !== "is") {
        params.user_id_operator = userOp;
      }
    } else if (computedUserId.value) {
      params.user_id = computedUserId.value;
      if (userOp !== "is") {
        params.user_id_operator = userOp;
      }
    }
    if (props.dashboardFilters && Object.keys(props.dashboardFilters).length > 0) {
      params.dashboard_filters = JSON.stringify(props.dashboardFilters);
    }

    const response = await AnalyticService.V3.getDashboardConfigurationByID(
      dashboardId,
      params,
    );

    if (response?.success && response?.data) {
      const config = response.data;

      if (config.chart_type) {
        chartType.value = `${config.chart_type}_chart`;
      }

      chartData.value = config.chart_data
        ? typeof config.chart_data === "string"
          ? JSON.parse(config.chart_data)
          : config.chart_data
        : {};

      chartConfig.value = config.chart_configuration
        ? typeof config.chart_configuration === "string"
          ? JSON.parse(config.chart_configuration)
          : config.chart_configuration
        : null;

      configLoaded.value = true;
      chartTotal = config.chart_data?.pagination?.total || 0;
    }
  } catch (error) {
    console.error(
      "An error occurred while fetching dashboard configuration: ",
      error,
    );
  } finally {
    if (!silent) {
      isLoading.value = false;
      eventBus.emit("chart-data-loaded", {
        key: `${props.rowIndex}_${props.sectionIndex}`,
        total: chartTotal,
      });
    }
  }
};

watch(
  () => props.section.config,
  (newConfig, oldConfig) => {
    cardName.value = newConfig?.card_name || "";
    selectedDashboardID.value = newConfig?.dashboard_id || null;

    if (isEditing.value) return;

    const dashboardChanged =
      newConfig?.dashboard_id !== oldConfig?.dashboard_id;
    if (
      dashboardChanged &&
      newConfig?.dashboard_id &&
      dashboardsList.value?.length > 0
    ) {
      if (currentChartID.value) {
        toFetchChartByID();
      } else if (currentConfigurationID.value) {
        toGetDashboardConfigurationByID();
      }
    }
  },
  { deep: true, immediate: true },
);

watch(
  [() => computedUserId.value, () => props.globalUserIds, () => props.globalUserOperator],
  () => {
    if (
      props.section.config?.dashboard_id &&
      !isEditing.value
    ) {
      if (currentConfigurationID.value) {
        toGetDashboardConfigurationByID();
      }
    }
  },
);

watch(
  () => props.dashboardFilters,
  () => {
    if (
      props.section.config?.dashboard_id &&
      !isEditing.value
    ) {
      if (currentConfigurationID.value) {
        toGetDashboardConfigurationByID();
      }
    }
  },
  { deep: true },
);

const handlePageChange = (paginationInfo) => {
  toGetDashboardConfigurationByID(
    { page: paginationInfo.page, page_size: paginationDefaults.pageSize },
    { silent: true },
  );
};

onMounted(() => {
  toFetchDashboards();
});
</script>

<style scoped>
#analytics_dashboard_card :deep(.apexcharts-legend) {
  max-height: 150px !important;
  overflow-y: auto !important;
}
</style>
